import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  LuCheck,
  LuCircleAlert,
  LuClock,
  LuCode,
  LuCrosshair,
  LuCylinder,
  LuDatabase,
  LuFileMinus2,
  LuFileOutput,
  LuFilePen,
  LuFilePlus2,
  LuLoaderCircle,
  LuUser,
  LuWrench,
  LuX,
} from "react-icons/lu";
import * as darvinShared from "../../../../../darvin-shared/schema/zod/agent/message-types.schema";
import { colors, typography } from "../../../../design/tokens";
import { isToolsMessage } from "../../../../types/chatTypeGuards";
import {
  AssistantMessageAnimationStatus,
  ProjectChatAssistantMessage,
} from "../../../../view_stores/chat_messages/chatModels";
import { useChatMessagesActions } from "../../../../view_stores/chat_messages/useChatMessagesStore";

export function ProjectToolUsageMessage({
  message,
}: {
  message: ProjectChatAssistantMessage;
}) {
  const { onAssistantMessageAnimationFinished } = useChatMessagesActions();

  // Add synchronized spin animation
  React.useEffect(() => {
    const style = document.createElement("style");
    style.id = "sync-spinner-animation";
    if (!document.getElementById("sync-spinner-animation")) {
      style.textContent = `
        @keyframes sync-spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `;
      document.head.appendChild(style);
    }
  }, []);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [isAutoScrollEnabled, setIsAutoScrollEnabled] = useState(true);
  const isAutoScrollingRef = useRef(false);
  const userIsScrollingRef = useRef(false);
  const [shouldShowButton, setShouldShowButton] = useState(false);
  const [showTopFade, setShowTopFade] = useState(false);
  const [showBottomFade, setShowBottomFade] = useState(false);
  const [hasScrollbars, setHasScrollbars] = useState(false);

  useEffect(() => {
    if (
      message.animationStatus === AssistantMessageAnimationStatus.ANIMATING &&
      message.fullyGenerated
    ) {
      setTimeout(() => {
        onAssistantMessageAnimationFinished();
      }, 400);
    }
  }, [
    message.animationStatus,
    message.fullyGenerated,
    onAssistantMessageAnimationFinished,
  ]);

  const updateFadeStates = useCallback((container: HTMLDivElement) => {
    const isAtTop = container.scrollTop <= 1;
    const isAtBottom =
      Math.abs(
        container.scrollHeight - container.scrollTop - container.clientHeight
      ) < 1;
    const hasScroll = container.scrollHeight > container.clientHeight;

    setShowTopFade(hasScroll && !isAtTop);
    setShowBottomFade(hasScroll && !isAtBottom);
    setHasScrollbars(hasScroll);
  }, []);

  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    const handleUserInteraction = () => {
      userIsScrollingRef.current = true;
      // Cancel auto-scroll if user interacts
      isAutoScrollingRef.current = false;
    };

    const handleScroll = (event: Event) => {
      const target = event.target as HTMLDivElement;

      // Update fade states
      updateFadeStates(target);

      // Process button logic if this is a user-initiated scroll
      // (either flagged by user interaction OR not from auto-scroll)
      const isUserScroll =
        userIsScrollingRef.current || !isAutoScrollingRef.current;

      if (isUserScroll && !isAutoScrollingRef.current) {
        // Check if user is at the bottom
        const isAtBottom =
          Math.abs(
            target.scrollHeight - target.scrollTop - target.clientHeight
          ) < 1;

        if (isAtBottom) {
          // User scrolled back to bottom, enable auto-scroll and hide button
          setIsAutoScrollEnabled(true);
          setShouldShowButton(false);
        } else {
          // User scrolled up, disable auto-scroll and show button
          setIsAutoScrollEnabled(false);
          setShouldShowButton(true);
        }
      }

      userIsScrollingRef.current = false;
    };

    container.addEventListener("scroll", handleScroll);
    container.addEventListener("wheel", handleUserInteraction);
    container.addEventListener("touchstart", handleUserInteraction);
    container.addEventListener("mousedown", handleUserInteraction);

    // Initial fade state check
    updateFadeStates(container);

    return () => {
      container.removeEventListener("scroll", handleScroll);
      container.removeEventListener("wheel", handleUserInteraction);
      container.removeEventListener("touchstart", handleUserInteraction);
      container.removeEventListener("mousedown", handleUserInteraction);
    };
  }, [updateFadeStates]);

  // ResizeObserver to detect content height changes and update fade states
  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    const resizeObserver = new ResizeObserver(() => {
      updateFadeStates(container);
    });

    resizeObserver.observe(container);
    // Also observe the scroll content to detect when items are added/removed
    const scrollContent = container.firstElementChild;
    if (scrollContent) {
      resizeObserver.observe(scrollContent);
    }

    return () => resizeObserver.disconnect();
  }, [updateFadeStates]);

  const toolUses = isToolsMessage(message.value)
    ? Object.entries(message.value.toolUses)
    : [];

  useEffect(() => {
    // Only auto-scroll if enabled
    if (!isAutoScrollEnabled) return;

    const container = scrollContainerRef.current;
    if (container) {
      isAutoScrollingRef.current = true;

      // Wait for DOM to update before scrolling
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          if (container) {
            // Scroll to the bottom of the container to show the last element
            container.scrollTo({
              top: container.scrollHeight,
              behavior: "smooth",
            });

            // Reset the flag and update fade states after animation completes
            setTimeout(() => {
              isAutoScrollingRef.current = false;
              // Re-evaluate fade states after scroll animation
              if (container) {
                updateFadeStates(container);
              }
            }, 500); // Scroll animation duration
          }
        });
      });
    }
  }, [isAutoScrollEnabled, toolUses.length, updateFadeStates]);

  const hasActiveTools = toolUses.some(
    ([, toolUse]) =>
      toolUse.status === darvinShared.ToolUseStatusEnum.enum.processing ||
      toolUse.status === darvinShared.ToolUseStatusEnum.enum.waiting
  );

  // Hide button when all files are done/errored
  useEffect(() => {
    if (!hasActiveTools && shouldShowButton) {
      setShouldShowButton(false);
    }
  }, [hasActiveTools, shouldShowButton]);

  // Re-evaluate fade states when all tools complete processing
  useEffect(() => {
    const container = scrollContainerRef.current;
    if (container && !hasActiveTools) {
      // Delay to ensure DOM has settled after status icon transitions
      requestAnimationFrame(() => {
        updateFadeStates(container);
      });
    }
  }, [hasActiveTools, updateFadeStates]);

  if (message.animationStatus === AssistantMessageAnimationStatus.WAITING) {
    return null;
  }

  return (
    <div
      className={`${
        message.animationStatus === AssistantMessageAnimationStatus.ANIMATING &&
        "message-container assistant-container"
      } w-full max-h-[230px] flex justify-start`}
    >
      <div
        className={`w-full py-3 px-5 break-words hyphens-auto message-bubble
        rounded-md flex flex-col gap-2 relative
      bg-dark-surface-secondary
      ${colors.text.primary} ${typography.size.base}
      border border-dark-border`}
      >
        {/* <strong>Processed files</strong> */}
        <div className="relative flex-1 min-h-0">
          {/* Top fade overlay */}
          {showTopFade && (
            <div className="absolute top-0 left-0 right-0 h-4 pointer-events-none z-10 bg-gradient-to-b from-dark-surface-secondary to-transparent" />
          )}

          <div
            ref={scrollContainerRef}
            className="flex flex-col gap-2 overflow-y-auto overflow-x-hidden px-1 h-full"
            style={{ scrollbarGutter: "stable" }}
          >
            {toolUses.map(([toolUseId, toolUseEntry]) => {
              const toolUse = toolUseEntry.toolUse;

              if (toolUse.toolType === "text_editor") {
                const textEditorTool =
                  toolUse as darvinShared.TextEditorToolUse;
                return (
                  <React.Fragment key={toolUseId}>
                    <FileItem
                      status={toolUseEntry.status}
                      changeType={textEditorTool.changeType}
                      filePath={textEditorTool.path}
                      purpose={textEditorTool.purpose}
                    />
                  </React.Fragment>
                );
              } else if (toolUse.toolType === "generic") {
                const genericTool = toolUse as darvinShared.GenericToolUse;
                return (
                  <React.Fragment key={toolUseId}>
                    <GenericToolItem
                      status={toolUseEntry.status}
                      type={genericTool.category}
                      name={genericTool.toolName}
                      purpose={genericTool.purpose}
                      params={genericTool.params}
                    />
                  </React.Fragment>
                );
              }

              return null;
            })}
          </div>

          {/* Bottom fade overlay */}
          {showBottomFade && (
            <div className="absolute bottom-0 left-0 right-0 h-4 pointer-events-none z-10 bg-gradient-to-t from-dark-surface-secondary to-transparent" />
          )}
        </div>
        {!isAutoScrollEnabled &&
          shouldShowButton &&
          hasActiveTools &&
          hasScrollbars && (
            <div className="flex items-center justify-center pr-3">
              <button
                onClick={() => {
                  const container = scrollContainerRef.current;
                  if (container) {
                    // Set flag to prevent scroll event handler from interfering
                    isAutoScrollingRef.current = true;
                    userIsScrollingRef.current = false;

                    // Immediately scroll to bottom
                    requestAnimationFrame(() => {
                      requestAnimationFrame(() => {
                        if (container) {
                          container.scrollTo({
                            top: container.scrollHeight,
                            behavior: "smooth",
                          });

                          // Reset the flag after animation completes
                          setTimeout(() => {
                            isAutoScrollingRef.current = false;
                          }, 1000);
                        }
                      });
                    });
                  }
                  setIsAutoScrollEnabled(true);
                  setShouldShowButton(false);
                }}
                className={`flex items-center gap-1 ${typography.size.sm}
              text-blue-400 hover:opacity-50 transition-opacity duration-300`}
              >
                <LuCrosshair size={16} />
                <span>Follow updates</span>
              </button>
            </div>
          )}
      </div>
    </div>
  );
}

// Helper function to capitalize first letter
const capitalizeFirstLetter = (str: string) => {
  return str.charAt(0).toUpperCase() + str.slice(1);
};

// Helper function to get status icon (shared between FileItem and AnalyzeItem)
const getStatusIcon = (status: darvinShared.ToolUseStatus) => {
  const animationDelay = (() => {
    const animationDuration = 1000; // 1 second
    const offset = -(Date.now() % animationDuration);
    return `${offset / 1000}s`;
  })();

  switch (status) {
    case darvinShared.ToolUseStatusEnum.enum.done:
      return <LuCheck className="text-dark-text" size={20} />;
    case darvinShared.ToolUseStatusEnum.enum.error:
      return <LuX className="text-dark-text" size={20} />;
    case darvinShared.ToolUseStatusEnum.enum.warning:
      return <LuCircleAlert className="text-dark-text" size={20} />;
    case darvinShared.ToolUseStatusEnum.enum.processing:
      return (
        <LuLoaderCircle
          className="text-dark-text opacity-50"
          size={20}
          style={{
            animation: "sync-spin 1s linear infinite",
            animationDelay: animationDelay,
            transformOrigin: "center",
          }}
        />
      );
    case darvinShared.ToolUseStatusEnum.enum.waiting:
      return <LuClock className="text-dark-text" size={20} />;
  }
};

const FileItem = React.memo(
  ({
    status,
    changeType,
    filePath,
    purpose,
  }: {
    status: darvinShared.ToolUseStatus;
    changeType: darvinShared.TextEditorChangeType;
    filePath?: string;
    purpose?: string;
  }) => {
    const statusIcon = getStatusIcon(status);
    purpose = purpose || capitalizeFirstLetter(changeType);

    const getChangeTypeIcon = () => {
      const iconSize = 16;
      const monochromeClass = `${colors.text.secondary} opacity-80`;
      switch (changeType) {
        case "new":
          return (
            <LuFilePlus2
              size={iconSize}
              className="text-blue-400 opacity-70"
              title="new"
            />
          );
        case "moved":
        case "modified":
          return (
            <LuFilePen
              size={iconSize}
              className={monochromeClass}
              title="modified"
            />
          );
        case "deleted":
          return (
            <LuFileMinus2
              size={iconSize}
              className={monochromeClass}
              title="deleted"
            />
          );
        case "viewed":
          return (
            <LuFileOutput
              size={iconSize}
              className={monochromeClass}
              title="viewed"
            />
          );
      }
    };

    return (
      <div className="flex items-start gap-2 pr-2 mb-0" data-status={status}>
        <div>{statusIcon}</div>
        <div className="flex flex-col leading-tight gap-1 min-w-0">
          <span
            className={`${typography.size.sm} ${typography.family.sans} ${colors.text.primary}`}
          >
            {purpose}
          </span>
          {filePath && (
            <div className="flex items-center gap-1 min-w-0">
              {getChangeTypeIcon()}
              <div className="relative overflow-hidden min-w-0 flex-1">
                <p
                  className={`${typography.size.sm} ${typography.family.sans} text-dark-placeholder whitespace-nowrap`}
                >
                  {filePath}
                </p>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }
);

const GenericToolItem = React.memo(
  ({
    status,
    purpose,
    type,
    name,
    params,
  }: {
    status: darvinShared.ToolUseStatus;
    purpose?: string;
    type: darvinShared.GenericToolCategory;
    name: string;
    params?: string;
  }) => {
    const statusIcon = getStatusIcon(status);
    const mainLine = purpose || `Using ${name} tool`;

    const getToolTypeIcon = () => {
      const iconSize = 16;
      const monochromeClass = `${colors.text.secondary} opacity-80`;
      const title = `${type}-tool`;
      switch (type) {
        case "code":
          return (
            <LuCode size={iconSize} className={monochromeClass} title={title} />
          );
        case "database":
          return (
            <LuDatabase
              size={iconSize}
              className={monochromeClass}
              title={title}
            />
          );
        case "auth":
          return (
            <LuUser size={iconSize} className={monochromeClass} title={title} />
          );
        case "storage":
          return (
            <LuCylinder
              size={iconSize}
              className={monochromeClass}
              title={title}
            />
          );
        case "other":
          return (
            <LuWrench
              size={iconSize}
              className={monochromeClass}
              title={title}
            />
          );
      }
    };

    return (
      <div className="flex items-start gap-2 pr-2 mb-0" data-status={status}>
        <div>{statusIcon}</div>
        <div className="flex flex-col leading-tight gap-1 min-w-0">
          <span
            className={`${typography.size.sm} ${typography.family.sans} ${colors.text.primary}`}
          >
            {mainLine}
          </span>
          {params && (
            <div className="flex items-center gap-1 min-w-0">
              {getToolTypeIcon()}
              <div className="relative overflow-hidden min-w-0 flex-1">
                <p
                  className={`${typography.size.sm} ${typography.family.sans} text-dark-placeholder whitespace-nowrap`}
                >
                  {params}
                </p>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }
);
